/**
 * OptiVehicle Profile — the ONE reusable vehicle-information experience,
 * mounted by Create Ticket and by the Ticket Command Center. Both read the
 * same canonical data (useVehicleProfile → vehicles + vehicle_identity_facts +
 * shop history); neither keeps its own copy of vehicle truth.
 *
 *   <OptiVehicleProfileCard>   compact summary + quick expansion + "View Full Vehicle Profile"
 *   <OptiVehicleProfileSheet>  the full profile (right sheet on desktop, full-width on phones)
 *
 * Every richer fact shows where it came from (NHTSA decoded / Shop entered /
 * External provider / OptiBay inferred / Shop record). Unknown reads Unknown.
 * A disagreeing source is shown as a conflict, never swapped in. Service
 * information is a corpus the identity points into (OSH-020); none is held
 * today, so the section says so instead of guessing.
 *
 * Configuration (Phase 2): curated factory configuration / ADAS reported for
 * the VIN by NHTSA, three-state — Equipped / Not equipped / Unknown. Only
 * positively known configuration shows by default; "Show all configuration"
 * reveals the rest. Unknown is never shown as "no". ADAS here is REPORTED
 * EQUIPMENT only — it never implies a calibration, a procedure or labor.
 *
 * Draft mode (Create Ticket, new VIN, vehicle not created yet): the decoded
 * facts are shown read-only, marked "not saved yet"; the trusted server records
 * its own decode when the vehicle is created.
 */
import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useToast } from '@/hooks/use-toast';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { actsAsLegacyRole } from '@/lib/roles/multiRole';
import { AlertTriangle, Car, Check, ChevronDown, ChevronUp, History, Loader2, Pencil, RefreshCw, Radar } from 'lucide-react';
import {
  historySummary, useTicketVehicleId, useVehicleProfile, useVehicleProfileActions, type VehicleProfileData,
} from '@/hooks/useVehicleProfile';
import {
  adasPresent, configurationGroups, isPositivelyKnown, resolveProfile, summarize,
  type ResolvedFact, type StoredFact, type VehicleBasics,
} from '../../../supabase/functions/_shared/vehicleProfile/facts';
import type { DecodedFact } from '../../../supabase/functions/_shared/vehicleProfile/vpic';
// NHTSA → "Reported equipped / Reported not equipped"; Shop entered → "Equipped / Not equipped"
import { displayFactValue } from './factDisplay';
import { entryBelowLatest, latestMileage, MILEAGE_FLAG_TEXT, type MileageObservation } from './mileage';

export interface DraftProfile {
  facts: DecodedFact[];
  quality: 'high' | 'partial';
  basics: VehicleBasics;
}

interface CommonProps {
  shopId: string;
  /** A persisted vehicle. */
  vehicleId?: string | null;
  /** Command Center: the ticket being viewed (resolves the vehicle; marks "This ticket" in history). */
  ticketId?: string | null;
  /** Create Ticket, new VIN not yet saved. */
  draft?: DraftProfile | null;
  /** Already-permitted display values (the caller applies its PII rule). */
  vin?: string | null;
  plate?: string | null;
  canEdit: boolean;
  context: 'create' | 'command';
}

const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

/** The profile view-model for either mode — the same resolution either way. */
function useProfileModel(props: CommonProps) {
  const ticketVehicle = useTicketVehicleId(props.ticketId, props.vehicleId ?? null);
  const vehicleId = props.vehicleId ?? ticketVehicle.data ?? null;
  const q = useVehicleProfile(props.draft ? null : vehicleId);
  const draftModel = useMemo(() => {
    if (!props.draft) return null;
    const facts: StoredFact[] = props.draft.facts.map((f) => ({ fact_key: f.key, value: f.value, source_class: 'decoded', source: 'nhtsa_vpic', confidence: props.draft!.quality }));
    const resolved = resolveProfile(facts, props.draft.basics);
    return { resolved, summary: summarize(resolved), history: [], recommendations: [], hasDecodedFacts: facts.length > 0 };
  }, [props.draft]);
  return { vehicleId, q, draftModel, data: (draftModel ?? q.data ?? null) as (Pick<VehicleProfileData, 'resolved' | 'summary' | 'history' | 'recommendations' | 'hasDecodedFacts'> & Partial<VehicleProfileData>) | null };
}

function SourceBadge({ fact }: { fact: ResolvedFact }) {
  if (!fact.sourceLabel) return null;
  return (
    <Badge variant="outline" className="text-[10px] font-normal whitespace-nowrap" data-testid={`fact-source-${fact.key}`}>
      {fact.sourceLabel}{fact.confidence === 'partial' ? ' · partial decode' : ''}
    </Badge>
  );
}

function FactValue({ fact }: { fact: ResolvedFact }) {
  if (fact.value == null) {
    // configuration: say WHY it is unknown (never a "no")
    const why = fact.spec.section === 'configuration' || fact.spec.equipment ? ' — NHTSA did not report this' : '';
    return <span className="text-muted-foreground italic" data-testid={`fact-value-${fact.key}`}>Unknown{why}</span>;
  }
  return <span data-testid={`fact-value-${fact.key}`}>{displayFactValue(fact.key, fact.value)}{fact.spec.unit ? ` ${fact.spec.unit}` : ''}</span>;
}

function FactRow({ fact, editable, onSave, onRetract, busy }: {
  fact: ResolvedFact; editable: boolean; busy: boolean;
  onSave: (key: string, value: string) => Promise<void>; onRetract: (key: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const input = fact.spec.input;
  return (
    <div className="py-2 border-b last:border-b-0" data-testid={`fact-row-${fact.key}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground">{fact.spec.label}</div>
          <div className="text-sm font-medium break-words"><FactValue fact={fact} /></div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <SourceBadge fact={fact} />
          {editable && !editing && (
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" data-testid={`fact-edit-${fact.key}`}
              onClick={() => { setDraft(fact.value ?? ''); setEditing(true); }}>
              <Pencil className="h-3 w-3 mr-1" />{fact.value == null ? 'Add' : 'Edit'}
            </Button>
          )}
          {editable && !editing && fact.hasAssertion && !fact.spec.column && (
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" data-testid={`fact-retract-${fact.key}`} disabled={busy}
              onClick={() => onRetract(fact.key)}>Remove shop value</Button>
          )}
        </div>
      </div>
      {fact.conflicts.map((c, i) => (
        <div key={i} className="mt-1 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400" data-testid={`fact-conflict-${fact.key}`}>
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>{c.sourceLabel}: <span className="font-medium">{displayFactValue(fact.key, c.value)}</span> — differs; the {fact.sourceLabel ?? 'current'} value stays authoritative until a person resolves it.</span>
        </div>
      ))}
      {editing && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {input.kind === 'select' ? (
            <Select value={draft} onValueChange={setDraft}>
              <SelectTrigger className="h-8 w-full sm:w-64" data-testid={`fact-input-${fact.key}`}><SelectValue placeholder="Choose…" /></SelectTrigger>
              <SelectContent>{input.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          ) : (
            <Input className="h-8 w-full sm:w-64" value={draft} onChange={(e) => setDraft(e.target.value)} data-testid={`fact-input-${fact.key}`}
              inputMode={input.kind === 'text' ? 'text' : 'decimal'} />
          )}
          <Button size="sm" className="h-8" disabled={busy || !draft.trim()} data-testid={`fact-save-${fact.key}`}
            onClick={async () => { await onSave(fact.key, draft); setEditing(false); }}>Save</Button>
          <Button size="sm" variant="outline" className="h-8" onClick={() => setEditing(false)}>Cancel</Button>
        </div>
      )}
    </div>
  );
}

function HistorySummaryLine({ data, ticketId }: { data: { history: VehicleProfileData['history'] }; ticketId?: string | null }) {
  const s = historySummary(data.history, ticketId);
  if (!s) return <p className="text-xs text-muted-foreground" data-testid="optivehicle-history-summary">No prior visits on record</p>;
  return (
    <p className="text-xs text-muted-foreground" data-testid="optivehicle-history-summary">
      <History className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
      Last visit: {fmtDate(s.lastDate)}{s.lastOdometer != null ? ` • ${s.lastOdometer.toLocaleString()} mi` : ''} · {s.count} prior visit{s.count === 1 ? '' : 's'}
    </p>
  );
}

/** Another active record of this shop has the same verified VIN — a duplicate awaiting review. */
function DuplicateNotice({ count }: { count: number }) {
  const { membership } = useAuth();
  const canReview = actsAsLegacyRole(membership as never, 'OWNER', 'MANAGER', 'ORG_ADMIN');
  return (
    <div className="flex flex-wrap items-start gap-1 text-xs text-amber-700 dark:text-amber-400" data-testid="optivehicle-duplicate-notice">
      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
      <span>
        {count === 1 ? 'Another vehicle record' : `${count} other vehicle records`} in this shop {count === 1 ? 'has' : 'have'} the same VIN.
        {' '}{canReview
          ? <Link to="/vehicle-duplicates" className="underline font-medium" data-testid="optivehicle-duplicate-review-link">Review duplicates</Link>
          : 'A manager can review and merge them.'}
      </span>
    </div>
  );
}

const sourceText = (o: MileageObservation) => (o.source === 'imported' ? `${o.label} (previous system)` : o.label);

/** Latest known mileage — the newest OBSERVED reading, with its date and where it came from. */
function MileageLine({ mileage }: { mileage: MileageObservation[] | undefined }) {
  const latest = latestMileage(mileage ?? []);
  if (!latest) return <p className="text-xs text-muted-foreground" data-testid="optivehicle-mileage-latest">Mileage: none on record</p>;
  return (
    <div className="text-sm" data-testid="optivehicle-mileage-latest">
      <span className="font-medium">{latest.miles.toLocaleString()} mi</span>
      <span className="text-xs text-muted-foreground"> · {fmtDate(latest.observedAt)} · {sourceText(latest)}{latest.corrected ? ' · corrected' : ''}</span>
      {latest.flag && (
        <div className="flex items-start gap-1 text-xs text-amber-700 dark:text-amber-400" data-testid="optivehicle-mileage-latest-flag">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />{MILEAGE_FLAG_TEXT[latest.flag]}
        </div>
      )}
    </div>
  );
}

/**
 * Create Ticket, beside the odometer input of a RETURNING vehicle: the last
 * recorded reading (date + source), and — without blocking — a note when the
 * entry is lower than it. Reads the same cached profile the card shows.
 */
export function OdometerEntryHint({ vehicleId, entered }: { vehicleId: string | null | undefined; entered: number | null | undefined }) {
  const q = useVehicleProfile(vehicleId ?? null);
  const latest = latestMileage(q.data?.mileage ?? []);
  if (!vehicleId || !latest) return null;
  return (
    <div className="space-y-0.5" data-testid="odometer-last-recorded">
      <p className="text-xs text-muted-foreground">
        Last recorded: <span className="font-medium text-foreground">{latest.miles.toLocaleString()} mi</span> · {fmtDate(latest.observedAt)} · {sourceText(latest)}
      </p>
      {entryBelowLatest(entered, latest) && (
        <p className="flex items-start gap-1 text-xs text-amber-700 dark:text-amber-400" data-testid="odometer-entry-below-latest">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />Lower than the last recorded reading — check the entry. It can still be saved.
        </p>
      )}
    </div>
  );
}

// ═══ configuration (Phase 2) ════════════════════════════════════════════════
function ConfigurationSection({ resolved, hasDecodedFacts, editable, busy, onSave, onRetract }: {
  resolved: readonly ResolvedFact[]; hasDecodedFacts: boolean; editable: boolean; busy: boolean;
  onSave: (key: string, value: string) => Promise<void>; onRetract: (key: string) => Promise<void>;
}) {
  const [showAll, setShowAll] = useState(false);
  const groups = configurationGroups(resolved);
  const all = groups.flatMap((g) => g.items);
  const known = all.filter((i) => isPositivelyKnown(i.state));
  const more = all.length - known.length;
  return (
    <section data-testid="optivehicle-configuration">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <h3 className="text-sm font-semibold">Configuration</h3>
        <Button type="button" size="sm" variant="ghost" className="h-8 px-2 text-xs" data-testid="optivehicle-config-toggle" onClick={() => setShowAll((v) => !v)}>
          {showAll ? 'Show known configuration only' : `Show all configuration${more > 0 ? ` (${more} more)` : ''}`}
        </Button>
      </div>
      {known.length === 0 && !showAll && (
        <p className="text-sm text-muted-foreground" data-testid="optivehicle-config-empty">
          No configuration has been reported for this vehicle.
          {hasDecodedFacts ? ' Vehicles decoded before configuration support can be refreshed with Re-decode VIN.' : ''}
        </p>
      )}
      <div className="space-y-3">
        {groups.map((g) => {
          const items = showAll ? g.items : g.items.filter((i) => isPositivelyKnown(i.state));
          if (items.length === 0) return null;
          return (
            <div key={g.group} data-testid={`optivehicle-config-group-${g.group}`}>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{g.label}</h4>
              {showAll
                ? items.map((i) => <FactRow key={i.fact.key} fact={i.fact} editable={editable} busy={busy} onSave={onSave} onRetract={onRetract} />)
                : (
                  <ul className="divide-y">
                    {items.map((i) => (
                      <li key={i.fact.key} className="flex flex-wrap items-start justify-between gap-2 py-1.5 text-sm" data-testid={`config-${i.fact.key}`}>
                        <span className="flex min-w-0 items-start gap-1.5">
                          {i.state === 'present' && <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" aria-label={i.fact.sourceClass === 'asserted' ? 'Equipped' : 'Reported equipped'} />}
                          <span className="break-words">
                            {i.state === 'present'
                              ? i.fact.spec.label
                              : <>{i.fact.spec.label}: <span className="font-medium"><FactValue fact={i.fact} /></span></>}
                          </span>
                        </span>
                        <SourceBadge fact={i.fact} />
                      </li>
                    ))}
                  </ul>
                )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ═══ card ════════════════════════════════════════════════════════════════════
export function OptiVehicleProfileCard(props: CommonProps) {
  const [quickOpen, setQuickOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const { q, draftModel, data } = useProfileModel(props);
  const loading = !draftModel && q.isLoading;
  if (!draftModel && !props.vehicleId && !props.ticketId) return null;

  const decodedCount = data?.resolved.filter((r) => r.sourceClass === 'decoded').length ?? 0;
  const status = props.draft ? 'NHTSA decoded · not saved yet' : data?.hasDecodedFacts ? 'NHTSA decoded' : 'Not decoded';
  const quick = data?.resolved.filter((r) => r.spec.quick || r.key === 'trim') ?? [];

  return (
    <div className="rounded-lg border bg-muted/20 p-3 space-y-2" data-testid="optivehicle-card">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex items-start gap-2">
          <Car className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">OptiVehicle Profile</div>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
              <>
                <div className="font-medium break-words" data-testid="optivehicle-title">{data?.summary.title}</div>
                {data?.summary.specLine && <div className="text-sm text-muted-foreground break-words" data-testid="optivehicle-specline">{data.summary.specLine}</div>}
              </>
            )}
          </div>
        </div>
        <Badge variant={data?.hasDecodedFacts ? 'secondary' : 'outline'} className="text-[10px] shrink-0" data-testid="optivehicle-source">{status}</Badge>
      </div>
      {data && adasPresent(data.resolved).length > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="optivehicle-adas-indicator">
          <Radar className="h-3.5 w-3.5" />ADAS: {adasPresent(data.resolved).length} system{adasPresent(data.resolved).length === 1 ? '' : 's'} reported
        </div>
      )}
      {!props.draft && data?.mergedFromVehicleId && (
        <p className="text-xs text-muted-foreground" data-testid="optivehicle-merged-redirect">
          This record was merged into the vehicle shown here; its history now lives on this profile.
        </p>
      )}
      {!props.draft && (data?.duplicateCount ?? 0) > 0 && <DuplicateNotice count={data!.duplicateCount!} />}
      {!props.draft && data && <MileageLine mileage={data.mileage} />}
      {!props.draft && data && <HistorySummaryLine data={data as VehicleProfileData} ticketId={props.ticketId} />}
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="ghost" className="h-8 px-2 text-xs" data-testid="optivehicle-expand" onClick={() => setQuickOpen((o) => !o)}>
          {quickOpen ? <ChevronUp className="h-3.5 w-3.5 mr-1" /> : <ChevronDown className="h-3.5 w-3.5 mr-1" />}
          {quickOpen ? 'Less' : 'Quick view'}
        </Button>
        <Button type="button" size="sm" variant="outline" className="h-8 text-xs" data-testid="optivehicle-open-full" onClick={() => setSheetOpen(true)}>
          View Full Vehicle Profile
        </Button>
      </div>
      {quickOpen && data && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 pt-1" data-testid="optivehicle-quick">
          {quick.map((f) => (
            <div key={f.key} className="flex items-baseline justify-between gap-2 text-sm" data-testid={`quick-${f.key}`}>
              <span className="text-xs text-muted-foreground">{f.spec.label}</span>
              <span className="text-right"><FactValue fact={f} /></span>
            </div>
          ))}
          {decodedCount === 0 && !props.draft && <p className="text-xs text-muted-foreground sm:col-span-2">Not decoded — open the full profile to decode the VIN.</p>}
        </div>
      )}
      <OptiVehicleProfileSheet {...props} open={sheetOpen} onOpenChange={setSheetOpen} />
    </div>
  );
}

// ═══ full profile ═══════════════════════════════════════════════════════════
export function OptiVehicleProfileSheet(props: CommonProps & { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { toast } = useToast();
  const { vehicleId, q, draftModel, data } = useProfileModel(props);
  const actions = useVehicleProfileActions(props.shopId, vehicleId);
  const persisted = !draftModel && !!vehicleId;
  const editable = props.canEdit && persisted;
  const busy = actions.assert.isPending || actions.retract.isPending || actions.decode.isPending;

  const onSave = async (key: string, value: string) => {
    try { await actions.assert.mutateAsync({ factKey: key, value }); toast({ title: 'Saved', description: 'Recorded as Shop entered.' }); }
    catch (e) { toast({ title: 'Not saved', description: (e as Error).message, variant: 'destructive' }); throw e; }
  };
  const onRetract = async (key: string) => {
    try { await actions.retract.mutateAsync(key); } catch (e) { toast({ title: 'Not changed', description: (e as Error).message, variant: 'destructive' }); }
  };
  const onDecode = async () => {
    try {
      const r = await actions.decode.mutateAsync();
      toast({ title: r?.status === 'decoded' ? 'VIN decoded' : 'Nothing decoded', description: r?.status === 'decoded' ? 'NHTSA facts recorded.' : 'NHTSA returned no usable identity for this VIN.' });
    } catch (e) { toast({ title: 'Decode failed', description: (e as Error).message, variant: 'destructive' }); }
  };

  const section = (s: 'identity' | 'powertrain') => (data?.resolved ?? []).filter((r) => r.spec.section === s);
  const hasVin = !!props.vin || !!(data as VehicleProfileData | null)?.basics?.has_vin;

  return (
    <Sheet open={props.open} onOpenChange={props.onOpenChange}>
      {/* z-[9999]: the profile also opens from INSIDE the Ticket Command Center dialog (z-[9999]);
          portalled after it, the sheet stacks on top, and its dropdowns (z-[10000]) still render above it. */}
      <SheetContent side="right" className="z-[9999] w-full sm:max-w-none lg:max-w-xl p-0 flex flex-col" overlayClassName="z-[9999]" data-testid="optivehicle-sheet">
        <SheetHeader className="px-4 sm:px-6 pt-6 pb-3 border-b text-left">
          <SheetTitle className="pr-8 break-words" data-testid="optivehicle-sheet-title">{data?.summary.title ?? 'Vehicle'}</SheetTitle>
          <SheetDescription className="break-words">{data?.summary.specLine || 'OptiVehicle Profile'}</SheetDescription>
          <div className="flex flex-wrap gap-2 pt-1">
            {props.draft && <Badge variant="secondary">NHTSA decoded · not saved yet</Badge>}
            {editable && hasVin && (
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={onDecode} disabled={busy} data-testid="optivehicle-decode">
                <RefreshCw className="h-3.5 w-3.5 mr-1" />{data?.hasDecodedFacts ? 'Re-decode VIN' : 'Decode VIN'}
              </Button>
            )}
          </div>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-6">
          {!draftModel && q.isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
            <>
              {data && adasPresent(data.resolved).length > 0 && (
                <div className="rounded-md border bg-muted/30 p-3 text-sm" data-testid="optivehicle-adas-summary">
                  <div className="flex items-center gap-1.5 font-medium"><Radar className="h-4 w-4" />ADAS reported: {adasPresent(data.resolved).join(' · ')}</div>
                  <p className="mt-1 text-xs text-muted-foreground">Factory-reported equipment for this VIN. It does not by itself mean a calibration is required — that depends on the repair and on licensed service information. Verify the equipment on the vehicle before relying on it for service or calibration decisions.</p>
                </div>
              )}
              <section data-testid="optivehicle-section-identity">
                <h3 className="text-sm font-semibold mb-1">Identity &amp; Build</h3>
                {(props.vin || props.plate) && (
                  <div className="py-2 border-b text-sm space-y-1">
                    {props.vin && <div><span className="text-xs text-muted-foreground mr-2">VIN</span><span className="font-mono break-all">{props.vin}</span></div>}
                    {props.plate && <div><span className="text-xs text-muted-foreground mr-2">Plate</span><span className="font-mono">{props.plate}</span></div>}
                  </div>
                )}
                {section('identity').map((f) => <FactRow key={f.key} fact={f} editable={editable} busy={busy} onSave={onSave} onRetract={onRetract} />)}
              </section>
              <section data-testid="optivehicle-section-powertrain">
                <h3 className="text-sm font-semibold mb-1">Powertrain</h3>
                {section('powertrain').map((f) => <FactRow key={f.key} fact={f} editable={editable} busy={busy} onSave={onSave} onRetract={onRetract} />)}
              </section>
              <ConfigurationSection resolved={data?.resolved ?? []} hasDecodedFacts={!!data?.hasDecodedFacts} editable={editable} busy={busy} onSave={onSave} onRetract={onRetract} />
              <section data-testid="optivehicle-service-info">
                <h3 className="text-sm font-semibold mb-1">Service Information</h3>
                <p className="text-sm text-muted-foreground">OptiBay does not hold licensed service information for this vehicle. Fluids, capacities, torque specifications, maintenance intervals and procedures are never estimated — they will appear here, with their source, once a licensed corpus is connected.</p>
              </section>
              {!props.draft && (
                <section data-testid="optivehicle-history">
                  <h3 className="text-sm font-semibold mb-1">Shop History</h3>
                  {(data?.history.length ?? 0) === 0 && <p className="text-sm text-muted-foreground">No visits on record.</p>}
                  <ul className="space-y-2">
                    {data?.history.map((h) => (
                      <li key={h.ticketId} className="rounded-md border p-2 text-sm" data-testid={`history-visit-${h.ticketId}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="font-medium">{h.number}{h.ticketId === props.ticketId ? ' (this ticket)' : ''}</span>
                          <Badge variant="outline" className="text-[10px]">{h.status}</Badge>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {fmtDate(h.createdAt)}{h.completedAt ? ` → completed ${fmtDate(h.completedAt)}` : ''}{h.odometerMiles != null ? ` • ${h.odometerMiles.toLocaleString()} mi` : ''}
                        </div>
                        {h.jobs.length > 0 && <div className="text-xs mt-1 break-words">{h.jobs.join(' · ')}</div>}
                      </li>
                    ))}
                  </ul>
                  <section className="mt-4" data-testid="optivehicle-mileage">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Mileage</h4>
                    {(data?.mileage?.length ?? 0) === 0 ? (
                      <p className="text-sm text-muted-foreground">No odometer readings on record.</p>
                    ) : (
                      <ul className="space-y-1">
                        {data!.mileage!.slice(0, 12).map((o, i) => (
                          <li key={`${o.source}-${o.ticketId ?? o.label}-${i}`} className="text-sm" data-testid={`mileage-reading-${i}`}>
                            <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                              <span className="font-medium">{o.miles.toLocaleString()} mi</span>
                              <span className="text-xs text-muted-foreground">{fmtDate(o.observedAt)} · {sourceText(o)}</span>
                            </div>
                            {o.corrected && (
                              <div className="text-xs text-muted-foreground">Corrected {fmtDate(o.corrected.changedAt)} — first entered as {o.corrected.previousMiles.toLocaleString()} mi</div>
                            )}
                            {o.flag && (
                              <div className="flex items-start gap-1 text-xs text-amber-700 dark:text-amber-400" data-testid={`mileage-flag-${i}`}>
                                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />{MILEAGE_FLAG_TEXT[o.flag]}
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                  <section className="mt-4" data-testid="optivehicle-imported-history">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Imported history</h4>
                    {data?.importedUnavailable ? (
                      <p className="text-sm text-muted-foreground" data-testid="optivehicle-imported-unavailable">Imported history could not be loaded right now.</p>
                    ) : (data?.imported?.length ?? 0) === 0 ? (
                      <p className="text-sm text-muted-foreground">No repair orders from a previous system are linked to this vehicle.</p>
                    ) : (
                      <>
                        <p className="text-xs text-muted-foreground mb-2">From the shop's previous system, matched to this vehicle by exact VIN. These are imported records, not visits OptiBay observed.</p>
                        <ul className="space-y-2">
                          {data!.imported!.map((r) => (
                            <li key={r.id} className="rounded-md border border-dashed p-2 text-sm" data-testid={`imported-ro-${r.id}`}>
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <span className="font-medium">{r.roNumber ? `RO ${r.roNumber}` : 'Repair order'}</span>
                                {r.status && <Badge variant="outline" className="text-[10px]">{r.status}</Badge>}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {fmtDate(r.openedAt)}{r.closedAt ? ` → closed ${fmtDate(r.closedAt)}` : ''}
                                {(r.odometerOut ?? r.odometerIn) != null ? ` • ${(r.odometerOut ?? r.odometerIn)!.toLocaleString()} mi` : ''}
                              </div>
                              {r.jobs.length > 0 && (
                                <div className="text-xs mt-1 break-words">
                                  {r.jobs.map((j) => `${j.description}${j.isComeback ? ' (comeback)' : ''}${j.isWarranty ? ' (warranty)' : ''}`).join(' · ')}
                                </div>
                              )}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </section>
                  {(data?.recommendations.length ?? 0) > 0 && (
                    <div className="mt-3">
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Recommendations</h4>
                      <ul className="space-y-1">
                        {data!.recommendations.map((r, i) => (
                          <li key={i} className="text-sm flex flex-wrap justify-between gap-2"><span>{r.title}</span><Badge variant="outline" className="text-[10px]">{r.status}</Badge></li>
                        ))}
                      </ul>
                    </div>
                  )}
                </section>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
